import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { useSession } from './useSession';

export interface MfaStatus {
  /** true si auth.mfa_factors tiene un factor TOTP verified. */
  enrolled: boolean;
  /** Loading inicial. */
  loading: boolean;
  /** Refrescar manualmente (después de enroll). */
  refresh: () => void;
}

/**
 * Verifica si el usuario actual tiene MFA TOTP enrolado.
 *
 * NO confiable solo via JWT (app_metadata no trae mfa_enrolled). Consulta
 * auth.mfa_factors directamente vía supabase-js.
 *
 * Cache: ttl 30s en sessionStorage por user.id. Sin esto, cada navegación
 * entre routes del SPA dispara listFactors() — caro y compite con otras
 * llamadas Supabase Auth por el Web Lock interno.
 */
const MFA_CACHE_TTL_MS = 30_000;
const MFA_CACHE_KEY_PREFIX = 'aosenuma-cache:mfa-enrolled:';

function readMfaCache(userId: string): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(MFA_CACHE_KEY_PREFIX + userId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts?: number; enrolled?: boolean };
    if (parsed.ts != null && Date.now() - parsed.ts < MFA_CACHE_TTL_MS && typeof parsed.enrolled === 'boolean') {
      return parsed.enrolled;
    }
  } catch { /* corrupto */ }
  return null;
}

function writeMfaCache(userId: string, enrolled: boolean): void {
  if (typeof window === 'undefined') return;
  // Solo cacheamos enrolled=true. enrolled=false NO se cachea porque puede
  // ser transitorio (timeout en listFactors, Web Lock contention, network
  // glitch). Si lo cacheáramos, AuthGate vería !enrolled durante 30s y
  // dispararía MfaEnrollWizard → mfa.enroll() → 403 "AAL2 required to
  // enroll a new factor" (porque el factor verified SÍ existía en realidad,
  // solo listFactors falló). Mejor pagar el roundtrip extra que cachear
  // estado falso.
  if (!enrolled) return;
  try {
    window.sessionStorage.setItem(MFA_CACHE_KEY_PREFIX + userId, JSON.stringify({ ts: Date.now(), enrolled }));
  } catch { /* QuotaExceeded */ }
}

export function useMfaStatus(): MfaStatus {
  const { user } = useSession();
  const userId = user?.id ?? null;

  // Hidratación sincrónica desde el cache si hay entry < ttl.
  const [enrolled, setEnrolled] = useState<boolean>(() => {
    if (!userId) return false;
    return readMfaCache(userId) ?? false;
  });
  const [loading, setLoading] = useState<boolean>(() => {
    if (!userId) return false;
    return readMfaCache(userId) == null;
  });
  const [tick, setTick] = useState(0);

  // Si cambia el user, re-hidratar desde cache.
  const prevUserIdRef = useRef<string | null>(userId);
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      if (!userId) {
        setEnrolled(false);
        setLoading(false);
      } else {
        const cached = readMfaCache(userId);
        if (cached != null) {
          setEnrolled(cached);
          setLoading(false);
        } else {
          setLoading(true);
        }
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!isSupabaseConfigured() || !userId) {
      return;
    }
    let cancel = false;
    const sb = getSupabase();

    // Safety timeout: si listFactors cuelga (token corrupto, network),
    // asumir no enrolled y seguir. Sin esto, el spinner queda infinito.
    const timeout = window.setTimeout(() => {
      if (cancel) return;
      // eslint-disable-next-line no-console
      console.warn('[useMfaStatus] listFactors timeout 3s — assuming not enrolled');
      setEnrolled(false);
      setLoading(false);
    }, 3000);

    void sb.auth.mfa.listFactors().then(({ data, error }) => {
      if (cancel) return;
      window.clearTimeout(timeout);
      if (error || !data) {
        setEnrolled(false);
        writeMfaCache(userId, false);
      } else {
        const totp = data.totp ?? [];
        const isEnrolled = totp.some((f) => f.status === 'verified');
        setEnrolled(isEnrolled);
        writeMfaCache(userId, isEnrolled);
      }
      setLoading(false);
    }).catch(() => {
      if (cancel) return;
      window.clearTimeout(timeout);
      setEnrolled(false);
      setLoading(false);
    });
    return () => {
      cancel = true;
      window.clearTimeout(timeout);
    };
  }, [userId, tick]);

  const refresh = useCallback(() => {
    if (userId && typeof window !== 'undefined') {
      try { window.sessionStorage.removeItem(MFA_CACHE_KEY_PREFIX + userId); } catch { /* ignore */ }
    }
    setTick((t) => t + 1);
  }, [userId]);

  return { enrolled, loading, refresh };
}
